import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import Header from './components/Header1';
import Footer from './components/Footer1';

const FAQs = () => {
    const navigate = useNavigate();
    const [openIndex, setOpenIndex] = useState(null);

    const faqs = [
        {
            category: 'Orders & Shopping',
            questions: [
                { question: 'How do I place an order?', answer: 'Browse our collection, add items to your cart, and proceed to checkout. You will need to create an account or sign in to complete your purchase. Follow the prompts to enter your shipping and payment information, then review and confirm your order.' },
                { question: 'Can I modify or cancel my order?', answer: 'Orders can be modified or cancelled within 2 hours of placement. After this window, orders enter our processing system. Please contact our customer service team immediately if you need to make changes.' },
                { question: 'What payment methods do you accept?', answer: 'We accept all major credit/debit cards, UPI, net banking, Paytm, and cash on delivery for orders within India. All payments are processed securely through encrypted connections.' },
                { question: 'Do you offer gift wrapping?', answer: 'Yes! We offer complimentary gift wrapping on all orders. Simply select the gift wrapping option during checkout and add a personalized message.' }
            ]
        },
        {
            category: 'Shipping & Delivery',
            questions: [
                { question: 'How long does shipping take?', answer: 'Standard shipping within India takes 5-7 business days. Express shipping takes 2-3 business days. Metro cities may receive orders 1-2 days earlier. Processing time is 1-2 business days.' },
                { question: 'Do you ship internationally?', answer: 'Yes, we ship to select countries worldwide. International shipping costs and delivery times vary by destination. Customs duties may apply and are the responsibility of the recipient.' },
                { question: 'How can I track my order?', answer: 'Once your order ships, you will receive a confirmation email with a tracking number. You can also track your order through your account dashboard on our website.' },
                { question: 'Is shipping free?', answer: 'We offer free standard shipping on all orders above \u20B95,000 within India. Orders below this amount incur a flat shipping fee of \u20B9199.' }
            ]
        },
        {
            category: 'Returns & Exchanges',
            questions: [
                { question: 'What is your return policy?', answer: 'We accept returns within 15 days of delivery. Items must be unworn, unwashed, and in original condition with tags attached. Sarees must have the fall and pico intact.' },
                { question: 'How do I initiate a return?', answer: 'Log in to your account, go to order history, select the order, and click "Request Return". We will email you a prepaid return label. Package items securely and drop off at any authorized location.' },
                { question: 'How long does a refund take?', answer: 'Once we receive your return, please allow 5-7 business days for processing. Refunds are issued to your original payment method and may take an additional 3-5 business days to appear.' },
                { question: 'Can I exchange for a different size?', answer: 'Yes! We offer free size exchanges. Contact our team within 15 days of delivery with your order number and preferred size.' }
            ]
        },
        {
            category: 'Products & Sizing',
            questions: [
                { question: 'How do I find my size?', answer: 'Each product page includes a detailed size guide with measurements in both inches and centimeters. We recommend measuring yourself and comparing to our charts. For sarees, one size fits all.' },
                { question: 'Are your products handcrafted?', answer: 'Yes, all our products are handcrafted by skilled artisans across India. Each piece may have slight variations that are a hallmark of handmade craftsmanship.' },
                { question: 'How should I care for my garments?', answer: 'Care instructions vary by fabric. Generally, we recommend dry cleaning for silk and embroidered pieces. Cotton items can be hand-washed in cold water. Always check the care label.' },
                { question: 'Do you offer customization?', answer: 'Yes, we offer blouse stitching and minor alterations for select products. Custom design requests can be discussed through our contact form.' }
            ]
        }
    ];

    const toggleFAQ = (index) => { setOpenIndex(openIndex === index ? null : index); };

    return (
        <div className="min-h-screen bg-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            <Header />

            {/* Hero */}
            <section className="bg-[#F8FAF8] py-12 md:py-16">
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-4">
                        <HelpCircle className="w-6 h-6 text-white" />
                    </div>
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#1A1A1A] mb-2">Frequently Asked Questions</h1>
                    <p className="text-sm md:text-base text-[#6B7280] max-w-lg mx-auto">Find answers to common questions about our products, shipping, and policies</p>
                </div>
            </section>

            {/* FAQs */}
            <section className="py-10 md:py-16">
                <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8">
                    {faqs.map((category, catIdx) => (
                        <div key={catIdx} className="mb-8 md:mb-10 last:mb-0">
                            <h2 className="text-lg md:text-xl font-semibold text-[#1A1A1A] mb-4">{category.category}</h2>
                            <div className="space-y-2 md:space-y-3">
                                {category.questions.map((faq, qIdx) => {
                                    const globalIndex = `${catIdx}-${qIdx}`;
                                    const isOpen = openIndex === globalIndex;
                                    return (
                                        <div key={qIdx} className="border border-[#E5E7EB] overflow-hidden">
                                            <button onClick={() => toggleFAQ(globalIndex)} className="w-full px-4 md:px-5 py-3 md:py-4 flex items-center justify-between text-left hover:bg-[#F8FAF8] transition-colors">
                                                <span className="text-sm md:text-base font-medium text-[#1A1A1A] pr-4">{faq.question}</span>
                                                {isOpen ? <ChevronUp className="w-4 h-4 text-[#6B7280] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#6B7280] flex-shrink-0" />}
                                            </button>
                                            {isOpen && (
                                                <div className="px-4 md:px-5 py-3 md:py-4 bg-[#F8FAF8] border-t border-[#E5E7EB]">
                                                    <p className="text-sm text-[#6B7280] leading-relaxed">{faq.answer}</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    {/* Contact */}
                    <div className="mt-10 md:mt-14 p-5 md:p-6 bg-[#F8FAF8] text-center">
                        <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">Still Have Questions?</h3>
                        <p className="text-sm text-[#6B7280] mb-4">Our customer service team is happy to help.</p>
                        <p className="text-sm font-medium text-[#1A1A1A] mb-4">Email: support@priya.com</p>
                        <button onClick={() => navigate('/template1/contact')} className="px-6 py-2.5 bg-[#2D6A4F] text-white text-sm font-medium hover:bg-[#1B4332] transition-colors">
                            Contact Us
                        </button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default FAQs;
